import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchAuditorProformas,
  fetchHospitalProformas,
  type ProformaApi,
  type ProformasQuery,
} from "../api/proformaApi";
import { getAuthToken, getStoredAccountType } from "../auth";
import { authorizationRequests, authorizationRequestSummary } from "../data";
import type { AuthorizationRequest } from "../types";

function mapApiToRequest(row: ProformaApi): AuthorizationRequest {
  const urgency =
    row.urgency === "urgent" || row.urgency === "emergency" ? "urgent" : "normal";
  const status =
    row.status === "approved" || row.status === "rejected" ? row.status : "pending";

  return {
    id: String(row.id),
    patient: row.patient || "",
    patientId: row.patient_id || "",
    packageCode: row.package_code || "",
    packageRef: row.package_ref || "",
    hospital: row.hospital || "",
    company: row.company || "",
    urgency,
    date: row.date || "",
    status,
    insurer: row.insurer_name || "",
    insurancePackage: row.insurance_package || "",
    // Échéance recalculée côté navigateur à partir du délai restant (indépendant du fuseau du serveur).
    dueAt:
      status === "pending" && row.remaining_seconds !== null && row.remaining_seconds !== undefined
        ? new Date(Date.now() + row.remaining_seconds * 1000).toISOString()
        : null,
    holdReason: row.hold_reason ?? null,
    chiefOnly: row.chief_only ?? false,
  };
}

export function useProformas(query: ProformasQuery = {}) {
  const [requests, setRequests] = useState<AuthorizationRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    const accountType = getStoredAccountType();
    const isHospital = accountType === "hospital";

    if (!getAuthToken()) {
      setUsingApi(false);
      setError(null);
      let mock = authorizationRequests;
      if (query.status) mock = mock.filter((r) => r.status === query.status);
      if (query.urgency) mock = mock.filter((r) => r.urgency === query.urgency);
      if (query.q) {
        const q = query.q.toLowerCase();
        mock = mock.filter((r) =>
          `${r.id} ${r.patient} ${r.patientId} ${r.packageCode} ${r.hospital} ${r.company}`
            .toLowerCase()
            .includes(q),
        );
      }
      setRequests(mock);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const api = isHospital
        ? await fetchHospitalProformas(query)
        : await fetchAuditorProformas(query);
      setRequests((api.proformas ?? []).map(mapApiToRequest));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Impossible de charger les demandes d'autorisation.",
      );
      setRequests([]);
      setUsingApi(false);
    } finally {
      setLoading(false);
    }
  }, [query.status, query.urgency, query.q]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary = useMemo(() => {
    if (!usingApi) return authorizationRequestSummary;
    return {
      total: requests.length,
      pending: requests.filter((r) => r.status === "pending").length,
      approved: requests.filter((r) => r.status === "approved").length,
      rejected: requests.filter((r) => r.status === "rejected").length,
    };
  }, [requests, usingApi]);

  return {
    requests,
    summary,
    loading,
    error,
    usingApi,
    reload: load,
  };
}
